import { StatusModal } from '@/components/StatusModal';
import { FormaSwitch } from '@/components/sklad/FormaSwitch';
import { MaterialReceiveRows } from '@/components/sklad/MaterialReceiveRows';
import { type PrintTarget, PrintTargetModal } from '@/components/sklad/PrintTargetModal';
import type { MaterialOrderItem } from '@/components/sklad/materialTypes';
import { tiskneNaZebru, zapamatujCilTisku } from '@/components/sklad/printRoll';
import { formatDate, formatQty } from '@/components/sklad/types';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useMemo, useState } from 'react';

type Item = {
  id: number;
  serial_number: number;
  latka_name: string;
  latka_type: string;
  latka_image: string;
  unit: string;
  quantity: number;
  received: number;
  received_at: number | null;
  /** 0 = role, 1 = přeložená na půl. Vyplňuje se při příjmu. */
  folded: number;
};

type Order = {
  id: number;
  supplier: string;
  note: string;
  expected_at: number | null;
  status: string;
  items: Item[];
  /** Drobný materiál — přijímá se bez štítku, jen se přičte na sklad. */
  materials: MaterialOrderItem[];
};

const CARD = 'bg-surface rounded-xl shadow-card';

/** Poslední příjem v objednávce (role i drobný materiál) — pro řazení tabu „Přijato". */
function lastReceivedAt(o: { shown: Item[]; shownMaterials: MaterialOrderItem[] }): number {
  return Math.max(
    0,
    ...o.shown.map((i) => i.received_at ?? 0),
    ...o.shownMaterials.map((m) => m.received_at ?? 0),
  );
}

function Thumb({ url }: { url: string }) {
  if (!url) return <div className="w-9 h-9 rounded bg-bg border border-border" />;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      className="w-9 h-9 rounded object-cover border border-border bg-bg"
    />
  );
}

/** Ikona v hlavičce objednávky — náklaďák na cestě, fajfka u přijaté. */
function OrderIcon({ done }: { done: boolean }) {
  return (
    <div
      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
        done ? 'bg-action' : 'bg-primary'
      }`}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth={done ? 2 : 1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {done ? (
          <polyline points="20 6 9 17 4 12" />
        ) : (
          <>
            <path d="M1 3h15v13H1z" />
            <path d="M16 8h4l3 3v5h-7V8z" />
            <circle cx="5.5" cy="18.5" r="2.5" />
            <circle cx="18.5" cy="18.5" r="2.5" />
          </>
        )}
      </svg>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="12" y1="18" x2="12" y2="12" />
      <polyline points="9 15 12 18 15 15" />
    </svg>
  );
}

/** Poznámka objednávky — klik na badge ji rozeditovává rovnou na místě. */
function NoteEditor({
  orderId,
  note,
  onSaved,
}: {
  orderId: number;
  note: string;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (value.trim() === note) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await apiFetch(`/api/sklad/orders/${orderId}/note`, {
        method: 'PATCH',
        body: { note: value.trim() },
      });
      setEditing(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <input
        // biome-ignore lint/a11y/noAutofocus: editace se otevírá kliknutím, fokus je očekávaný
        autoFocus
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') {
            setValue(note);
            setEditing(false);
          }
        }}
        placeholder="Poznámka…"
        className="text-[11px] font-semibold text-warning bg-warning-light border border-warning/40 rounded px-1.5 py-0.5 outline-none w-40 shrink-0"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setValue(note);
        setEditing(true);
      }}
      className={`text-[11px] font-semibold px-1.5 py-0.5 rounded shrink-0 cursor-pointer transition ${
        note
          ? 'text-warning bg-warning-light hover:brightness-95'
          : 'text-text-muted border border-dashed border-border hover:text-warning'
      }`}
    >
      {note || '+ Poznámka'}
    </button>
  );
}

/** Příjem materiálu — objednávky na cestě, příjem role a tisk štítku. */
export function SkladPrijemPage() {
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [tab, setTab] = useState<'pending' | 'received'>('pending');
  // Přepínač „Role / Složené" u nepřijatých rolí. Výchozí je role.
  const [folded, setFolded] = useState<Record<number, boolean>>({});
  // Kam tisknout se ptáme přes modal (stejně jako ve Skladu látek) — příjem
  // teď běžně chodí i z jiného PC, kde se hodí jiná volba než naposledy.
  const [printTarget, setPrintTarget] = useState<{
    key: number;
    items: Item[];
    supplier: string;
    title: string;
  } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusyId, setBulkBusyId] = useState<number | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ orders: Order[] }>('/api/sklad/orders');
      setOrders(d.orders);
    } catch (e: unknown) {
      setOrders([]);
      setToast({ msg: e instanceof ApiError ? e.message : 'Chyba načítání', kind: 'error' });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Tiskové věci (jsPDF + fonty) natáhneme dopředu, ať se po klepnutí na
  // „Tisk štítků" jen skládá PDF a nečeká se na síť.
  useEffect(() => {
    void import('@/components/sklad/labelsPdf').then((m) => m.prefetchLabelFonts());
  }, []);

  const all = useMemo(() => orders ?? [], [orders]);
  // „Na cestě" = objednávky s aspoň jednou nepřijatou rolí; „Přijato" naopak.
  const pending = useMemo(
    () =>
      all
        .map((o) => ({
          ...o,
          shown: o.items.filter((i) => !i.received),
          shownMaterials: o.materials.filter((m) => !m.received),
        }))
        .filter((o) => o.shown.length + o.shownMaterials.length > 0)
        .sort(
          (a, b) =>
            (a.expected_at ?? Number.POSITIVE_INFINITY) -
            (b.expected_at ?? Number.POSITIVE_INFINITY),
        ),
    [all],
  );
  const received = useMemo(
    () =>
      all
        .map((o) => ({
          ...o,
          shown: o.items.filter((i) => i.received),
          shownMaterials: o.materials.filter((m) => m.received),
        }))
        .filter((o) => o.shown.length + o.shownMaterials.length > 0)
        // Nejnovější příjem nahoře — podle nejpozdějšího received_at v objednávce.
        .sort((a, b) => lastReceivedAt(b) - lastReceivedAt(a)),
    [all],
  );
  const receivedCount = received.reduce((n, o) => n + o.shown.length + o.shownMaterials.length, 0);

  /** Štítky do PDF — pro celou objednávku i pro dotisk jedné role (stejný design). */
  async function makePdf(busyKey: number, items: Item[], supplier: string, target: PrintTarget) {
    setPdfBusyId(busyKey);
    try {
      const { generateLabelsPdf } = await import('@/components/sklad/labelsPdf');
      // U nepřijatých rolí ještě nic v databázi není — bereme, jak je zrovna
      // přepnutý přepínač Role/Složené.
      await generateLabelsPdf(
        items.map((i) => ({ ...i, folded: i.received ? i.folded : folded[i.id] ? 1 : 0 })),
        supplier,
        target,
      );
      if (target === 'fronta') setToast({ msg: 'Odesláno na Zebru', kind: 'success' });
    } catch (e: unknown) {
      // Důvod ukazujeme rovnou — jinak se na tabletu nedá zjistit, co selhalo.
      setToast({
        msg: `Nepodařilo se vygenerovat štítky: ${
          e instanceof ApiError ? e.describe() : e instanceof Error ? e.message : 'neznámá chyba'
        }`,
        kind: 'error',
      });
    } finally {
      setPdfBusyId(null);
    }
  }

  async function receive(item: Item) {
    setBusyId(item.id);
    try {
      await apiFetch(`/api/sklad/items/${item.id}/receive`, {
        method: 'POST',
        body: { folded: folded[item.id] === true },
      });
      await load();
      setToast({ msg: `Přijato: role č. ${item.serial_number}`, kind: 'success' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Chyba při příjmu', kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  /** Přijme všechny nepřijaté položky objednávky najednou — tisk se neřeší, jen sklad. */
  async function receiveAll(order: {
    id: number;
    shown: Item[];
    shownMaterials: MaterialOrderItem[];
  }) {
    setBulkBusyId(order.id);
    try {
      for (const item of order.shown) {
        await apiFetch(`/api/sklad/items/${item.id}/receive`, {
          method: 'POST',
          body: { folded: folded[item.id] === true },
        });
      }
      for (const m of order.shownMaterials) {
        await apiFetch(`/api/material/order-items/${m.id}/receive`, { method: 'POST' });
      }
      const n = order.shown.length + order.shownMaterials.length;
      setToast({
        msg: `Přijato ${n} ${n === 1 ? 'položka' : n <= 4 ? 'položky' : 'položek'}`,
        kind: 'success',
      });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Chyba při příjmu', kind: 'error' });
    } finally {
      await load();
      setBulkBusyId(null);
    }
  }

  async function unreceive(item: Item) {
    setBusyId(item.id);
    try {
      await apiFetch(`/api/sklad/items/${item.id}/unreceive`, { method: 'POST' });
      await load();
      setToast({ msg: `Role č. ${item.serial_number} vrácena na cestě`, kind: 'success' });
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Chyba při vracení', kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  const list = tab === 'pending' ? pending : received;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Příjem materiálu</h1>
        <p className="text-sm text-text-secondary mt-1">
          Objednávky na cestě. U každé látky klikni na „Přijato" — vytiskne se štítek a materiál se
          přidá na sklad.
        </p>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-border">
        {(
          [
            ['pending', 'Na cestě', pending.length],
            ['received', 'Přijato', receivedCount],
          ] as const
        ).map(([key, label, count]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`-mb-px px-4 py-2.5 text-sm font-semibold cursor-pointer border-b-2 transition-colors ${
              tab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text'
            }`}
          >
            {label}
            <span className="ml-1.5 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-bg text-text-muted">
              {count}
            </span>
          </button>
        ))}
      </div>

      {orders === null ? (
        <div className={`${CARD} p-10 text-center text-text-muted`}>Načítám…</div>
      ) : list.length === 0 ? (
        <div className={`${CARD} p-10 text-center text-sm text-text-muted`}>
          {tab === 'pending'
            ? 'Žádná objednávka není na cestě. 🎉'
            : 'Zatím nic přijatého. Přijaté role se objeví tady.'}
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((order) => (
            <div key={order.id} className={`${CARD} overflow-hidden`}>
              <div
                className={`flex items-center justify-between gap-3 px-5 py-3.5 border-b flex-wrap ${
                  tab === 'pending'
                    ? 'bg-primary-50 border-primary-100'
                    : 'bg-action-light border-border-light'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <OrderIcon done={tab === 'received'} />
                  <div className="min-w-0">
                    <div className="text-base font-bold text-text truncate">
                      {order.supplier || 'Bez dodavatele'}
                    </div>
                    <div className="flex items-center gap-2 min-w-0 mt-0.5">
                      <span className="text-xs text-text-secondary truncate">
                        Očekáváno: {formatDate(order.expected_at)}
                      </span>
                      <NoteEditor orderId={order.id} note={order.note} onSaved={load} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {tab === 'pending' && (
                    <>
                      <button
                        type="button"
                        onClick={() => receiveAll(order)}
                        disabled={bulkBusyId === order.id}
                        title="Přijmout všechny položky téhle objednávky najednou"
                        className="inline-flex items-center gap-1.5 bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-3 py-1.5 cursor-pointer transition-colors disabled:opacity-50"
                      >
                        <CheckIcon />
                        {bulkBusyId === order.id ? 'Přijímám…' : 'Přijmout vše'}
                      </button>
                      {order.shown.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setPrintTarget({
                              key: order.id,
                              items: order.shown,
                              supplier: order.supplier,
                              title: `${order.supplier || 'Bez dodavatele'} · ${order.shown.length} ${
                                order.shown.length === 1 ? 'role' : 'rolí'
                              }`,
                            })
                          }
                          disabled={pdfBusyId === order.id}
                          title="Vytisknout štítky celé objednávky (A6, jedna role = jedna stránka)"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-surface border border-primary-100 rounded-lg px-3 py-1.5 hover:bg-primary-100 cursor-pointer transition-colors disabled:opacity-50"
                        >
                          <PdfIcon />
                          {pdfBusyId === order.id ? 'Připravuji…' : 'Tisk štítků'}
                        </button>
                      )}
                    </>
                  )}
                  <span className="text-[11px] font-semibold text-text-secondary bg-surface border border-border-light px-2 py-0.5 rounded">
                    {order.items.filter((i) => i.received).length +
                      order.materials.filter((m) => m.received).length}
                    /{order.items.length + order.materials.length} přijato
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {order.shown.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-border-light last:border-0 hover:bg-surface-hover transition-colors"
                      >
                        <td className="px-4 py-2.5 w-14">
                          <Thumb url={item.latka_image} />
                        </td>
                        <td className="px-3 py-3 text-text font-bold w-16">{item.serial_number}</td>
                        <td className="px-3 py-3 text-text-secondary">{item.latka_name}</td>
                        <td className="px-3 py-3 text-text-muted">{item.latka_type || '—'}</td>
                        <td className="px-3 py-3 text-right text-text font-medium whitespace-nowrap">
                          {formatQty(item.quantity, item.unit)}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          {tab === 'pending' ? (
                            <FormaSwitch
                              value={folded[item.id] === true}
                              onChange={(v) => setFolded((prev) => ({ ...prev, [item.id]: v }))}
                            />
                          ) : (
                            <span className="text-text-secondary">
                              {item.folded ? 'Složené' : 'Role'}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {tab === 'pending' ? (
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setPrintTarget({
                                    key: item.id,
                                    items: [item],
                                    supplier: order.supplier,
                                    title: `Role č. ${item.serial_number} · ${item.latka_name}`,
                                  })
                                }
                                disabled={pdfBusyId === item.id || bulkBusyId === order.id}
                                title="Vytisknout štítek téhle role zvlášť, i bez příjmu"
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary border border-border rounded-lg px-3 py-2 hover:bg-surface-hover cursor-pointer transition-colors disabled:opacity-50"
                              >
                                <PdfIcon />
                                {pdfBusyId === item.id ? 'Připravuji…' : 'Tisk štítku'}
                              </button>
                              <button
                                type="button"
                                onClick={() => receive(item)}
                                disabled={busyId === item.id || bulkBusyId === order.id}
                                className="inline-flex items-center gap-1.5 bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-4 py-2 cursor-pointer transition-colors disabled:opacity-50"
                              >
                                <CheckIcon />
                                {busyId === item.id ? 'Přijímám…' : 'Přijato'}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-text-muted">
                                Přijato {formatDate(item.received_at)}
                              </span>
                              <button
                                type="button"
                                onClick={() => unreceive(item)}
                                disabled={busyId === item.id}
                                className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-3 py-2 hover:bg-warning-light hover:text-warning transition-colors cursor-pointer disabled:opacity-50"
                                title={'Vrátit roli zpět mezi nepřijaté (oprava překliku)'}
                              >
                                {busyId === item.id ? 'Vracím…' : 'Vrátit na cestě'}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setPrintTarget({
                                    key: item.id,
                                    items: [item],
                                    supplier: order.supplier,
                                    title: `Role č. ${item.serial_number} · ${item.latka_name}`,
                                  })
                                }
                                disabled={pdfBusyId === item.id}
                                title={'Znovu vytisknout štítek (stejný jako u nepřijatých)'}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary border border-border rounded-lg px-3 py-2 hover:bg-surface-hover cursor-pointer transition-colors disabled:opacity-50"
                              >
                                <PdfIcon />
                                {pdfBusyId === item.id ? 'Připravuji…' : 'Tisk štítku'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <MaterialReceiveRows
                items={order.shownMaterials}
                pending={tab === 'pending'}
                disabled={bulkBusyId === order.id}
                onDone={async (msg) => {
                  await load();
                  setToast({ msg, kind: 'success' });
                }}
                onError={(msg) => setToast({ msg, kind: 'error' })}
              />
            </div>
          ))}
        </div>
      )}

      {printTarget && (
        <PrintTargetModal
          title={printTarget.title}
          vychoziFronta={tiskneNaZebru()}
          onPick={(target) => {
            const pt = printTarget;
            setPrintTarget(null);
            // PDF je jednorázová kontrola, nic si z ní nepamatujeme.
            if (target !== 'pdf') zapamatujCilTisku(target === 'fronta');
            makePdf(pt.key, pt.items, pt.supplier, target);
          }}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
